import {
  type AccentColor,
  type EventStreamConfig,
  type JsonObject,
  getStreamConfig,
} from "@arti/dashboard-events";
import { useCallback, useEffect, useRef, useState } from "react";

import { getTaskEventsAsUser } from "../dashboard-client";
import { HYDRATION_EXCLUDE_EVENT_TYPES } from "../utils/providerProgressEventFilters";
import {
  appendProviderProgressEntry,
  buildHydratedProviderProgressEntries,
  toProviderProgressEventData,
} from "./providerProgressEntries";
import { useDashboardRealtime } from "./useDashboardRealtime";

/**
 * Provider progress event as received from the WebSocket
 */
export interface ProviderProgressEvent {
  task_id: string;
  execution_request_id?: string | null;
  node_id?: string | null;
  event_type: string;
  event_data: JsonObject;
  timestamp?: number;
  /** Event stream identifier (e.g., "main", "reviewer") */
  stream_id?: string | null;
}

/**
 * Stored provider progress entry with local timestamp and normalized stream_id
 */
export interface ProviderProgressEntry extends ProviderProgressEvent {
  received_at: number;
  /** Normalized stream ID - always present, defaults to "main" */
  stream_id: string;
}

export type HydrationStatus = "idle" | "loading" | "loaded" | "error";

/**
 * Dynamic stream configuration extracted from stream:started events.
 * Contains the same fields as EventStreamConfig plus agent metadata.
 */
export interface DynamicStreamConfig extends EventStreamConfig {
  /** Agent mode (implementation, review, etc.) */
  mode: string;
  /** Primary model identifier */
  model: string;
  /** Exploring model identifier */
  exploringModel: string | null;
}

interface ProviderProgressState {
  /** Map of task_id to array of progress events (most recent first) */
  eventsByTask: Map<string, ProviderProgressEntry[]>;
  /** Map of task_id to hydration status */
  hydrationStatus: Map<string, HydrationStatus>;
  /** Map of task_id to highest known persisted event ID (for deduplication) */
  lastPersistedId: Map<string, string>;
  /** Map of task_id -> stream_id -> DynamicStreamConfig (from stream:started events) */
  streamConfigsByTask: Map<string, Map<string, DynamicStreamConfig>>;
}

const INITIAL_STATE: ProviderProgressState = {
  eventsByTask: new Map(),
  hydrationStatus: new Map(),
  lastPersistedId: new Map(),
  streamConfigsByTask: new Map(),
};

const ACCENT_COLOR_LOOKUP: Record<AccentColor, true> = {
  amber: true,
  cyan: true,
  emerald: true,
  gray: true,
  indigo: true,
  rose: true,
  violet: true,
};

function isAccentColor(value: unknown): value is AccentColor {
  return typeof value === "string" && value in ACCENT_COLOR_LOOKUP;
}

/** Extract DynamicStreamConfig from a stream:started event */
function extractStreamConfig(
  event: ProviderProgressEntry,
): DynamicStreamConfig | null {
  if (event.event_type !== "stream:started") {
    return null;
  }

  const data = event.event_data;
  const label = typeof data.label === "string" ? data.label : event.stream_id;
  const accentColor = isAccentColor(data.accentColor)
    ? data.accentColor
    : "gray";
  const order = typeof data.order === "number" ? data.order : 999;
  const mode = typeof data.mode === "string" ? data.mode : "unknown";
  const model = typeof data.model === "string" ? data.model : "unknown";
  const exploringModel =
    typeof data.exploringModel === "string" ? data.exploringModel : null;

  return {
    accentColor,
    exploringModel,
    id: event.stream_id,
    label,
    mode,
    model,
    order,
  };
}

/**
 * Hook to track real-time provider progress events with hydration support.
 * Events can be hydrated from persisted storage and merged with real-time events.
 */
export function useProviderProgress(): {
  /** Get progress events for a specific task (chronological order - oldest first) */
  getEventsForTask: (taskId: string) => ProviderProgressEntry[];
  /** Get the latest event for a task */
  getLatestEvent: (taskId: string) => ProviderProgressEntry | null;
  /** Clear events for a specific task */
  clearTask: (taskId: string) => void;
  /** Clear all events */
  clearAll: () => void;
  /** Hydrate events for a task from persisted storage */
  hydrateTask: (taskId: string) => Promise<void>;
  /** Get hydration status for a task */
  getHydrationStatus: (taskId: string) => HydrationStatus;
  /** Get stream config for a specific stream (uses dynamic config if available, falls back to registry) */
  getStreamConfig: (
    taskId: string,
    streamId: string,
  ) => EventStreamConfig | DynamicStreamConfig;
  /** Get all stream configs for a task (dynamic configs merged with registry fallback) */
  getStreamConfigsForTask: (
    taskId: string,
  ) => Map<string, EventStreamConfig | DynamicStreamConfig>;
} {
  const [state, setState] = useState<ProviderProgressState>(INITIAL_STATE);
  const { subscribe } = useDashboardRealtime();

  useEffect(() => {
    const unsubscribe = subscribe(
      (message) => {
        const { type } = message;
        if (type !== "provider_progress") {
          return;
        }

        const event = message.payload;
        const taskId = typeof event.task_id === "string" ? event.task_id : null;
        const eventType =
          typeof event.event_type === "string" ? event.event_type : null;
        const eventData = toProviderProgressEventData(event.event_data);

        if (!taskId || !eventType) {
          return;
        }

        // Extract and normalize stream_id
        // Priority: payload.stream_id > event_data.streamId > "main"
        let streamId = "main";
        if (typeof event.stream_id === "string" && event.stream_id) {
          streamId = event.stream_id;
        } else if (
          typeof eventData.streamId === "string" &&
          eventData.streamId
        ) {
          ({ streamId } = eventData);
        }

        const entry: ProviderProgressEntry = {
          event_data: eventData,
          event_type: eventType,
          execution_request_id:
            typeof event.execution_request_id === "string"
              ? event.execution_request_id
              : null,
          node_id: typeof event.node_id === "string" ? event.node_id : null,
          received_at: Date.now(),
          stream_id: streamId,
          task_id: taskId,
          timestamp:
            typeof event.timestamp === "number" ? event.timestamp : undefined,
        };

        setState((current) => {
          const newEventsByTask = new Map(current.eventsByTask);
          const existing = newEventsByTask.get(taskId) ?? [];
          // Keep only the latest hidden usage snapshot per stream while
          // Preserving chronological order for all other progress events.
          const updated = appendProviderProgressEntry(existing, entry);
          newEventsByTask.set(taskId, updated);

          // Extract stream config from stream:started events
          let newStreamConfigsByTask = current.streamConfigsByTask;
          const streamConfig = extractStreamConfig(entry);
          if (streamConfig) {
            newStreamConfigsByTask = new Map(current.streamConfigsByTask);
            const taskConfigs = newStreamConfigsByTask.get(taskId) ?? new Map();
            const updatedTaskConfigs = new Map(taskConfigs);
            updatedTaskConfigs.set(streamId, streamConfig);
            newStreamConfigsByTask.set(taskId, updatedTaskConfigs);
          }

          return {
            ...current,
            eventsByTask: newEventsByTask,
            streamConfigsByTask: newStreamConfigsByTask,
          };
        });
      },
      { topic: "internalEventTopic" },
    );

    return () => unsubscribe();
  }, [subscribe]);

  const getEventsForTask = useCallback(
    (taskId: string): ProviderProgressEntry[] =>
      state.eventsByTask.get(taskId) ?? [],
    [state.eventsByTask],
  );

  const getLatestEvent = useCallback(
    (taskId: string): ProviderProgressEntry | null => {
      const events = state.eventsByTask.get(taskId);
      // Events are in chronological order, so latest is at the end
      return events && events.length > 0 ? events[events.length - 1] : null;
    },
    [state.eventsByTask],
  );

  const clearTask = useCallback((taskId: string) => {
    setState((current) => {
      const newEventsByTask = new Map(current.eventsByTask);
      const newHydrationStatus = new Map(current.hydrationStatus);
      const newLastPersistedId = new Map(current.lastPersistedId);
      const newStreamConfigsByTask = new Map(current.streamConfigsByTask);
      newEventsByTask.delete(taskId);
      newHydrationStatus.delete(taskId);
      newLastPersistedId.delete(taskId);
      newStreamConfigsByTask.delete(taskId);
      return {
        eventsByTask: newEventsByTask,
        hydrationStatus: newHydrationStatus,
        lastPersistedId: newLastPersistedId,
        streamConfigsByTask: newStreamConfigsByTask,
      };
    });
  }, []);

  const clearAll = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  // Track in-flight hydration requests to prevent duplicate calls
  const hydrationInFlight = useRef<Set<string>>(new Set());

  const hydrateTask = useCallback(
    async (taskId: string): Promise<void> => {
      // Prevent duplicate hydration requests
      if (hydrationInFlight.current.has(taskId)) {
        return;
      }

      // Check if already hydrated
      const currentStatus = state.hydrationStatus.get(taskId);
      if (currentStatus === "loaded" || currentStatus === "loading") {
        return;
      }

      hydrationInFlight.current.add(taskId);

      // Set loading status
      setState((current) => {
        const newHydrationStatus = new Map(current.hydrationStatus);
        newHydrationStatus.set(taskId, "loading");
        return { ...current, hydrationStatus: newHydrationStatus };
      });

      try {
        const eventsResult = await getTaskEventsAsUser({
          path: { taskId },
          query: {
            exclude_event_types:
              HYDRATION_EXCLUDE_EVENT_TYPES.length > 0
                ? HYDRATION_EXCLUDE_EVENT_TYPES.join(",")
                : undefined,
          },
        });
        const response = eventsResult.data;

        // Keep full transcript events, but compact hidden usage snapshots
        // To the latest event per stream before storing them.
        const hydratedEntries = buildHydratedProviderProgressEntries(
          response.events,
        );

        // Get the last persisted event ID for deduplication
        const lastId =
          response.events.length > 0
            ? response.events[response.events.length - 1].id
            : undefined;

        setState((current) => {
          const newEventsByTask = new Map(current.eventsByTask);
          const newHydrationStatus = new Map(current.hydrationStatus);
          const newLastPersistedId = new Map(current.lastPersistedId);
          const newStreamConfigsByTask = new Map(current.streamConfigsByTask);

          // Merge hydrated events with any existing real-time events
          // Hydrated events go first (older), real-time events go after (newer)
          const existingRealtime = newEventsByTask.get(taskId) ?? [];
          const merged = [...hydratedEntries, ...existingRealtime];
          newEventsByTask.set(taskId, merged);
          newHydrationStatus.set(taskId, "loaded");
          if (lastId !== undefined) {
            newLastPersistedId.set(taskId, lastId);
          }

          // Extract stream configs from hydrated stream:started events
          const taskConfigs = newStreamConfigsByTask.get(taskId) ?? new Map();
          const updatedTaskConfigs = new Map(taskConfigs);
          for (const entry of hydratedEntries) {
            const streamConfig = extractStreamConfig(entry);
            if (streamConfig) {
              updatedTaskConfigs.set(entry.stream_id, streamConfig);
            }
          }
          if (updatedTaskConfigs.size > 0) {
            newStreamConfigsByTask.set(taskId, updatedTaskConfigs);
          }

          return {
            eventsByTask: newEventsByTask,
            hydrationStatus: newHydrationStatus,
            lastPersistedId: newLastPersistedId,
            streamConfigsByTask: newStreamConfigsByTask,
          };
        });
      } catch (error) {
        console.error("Failed to hydrate task events:", error);
        setState((current) => {
          const newHydrationStatus = new Map(current.hydrationStatus);
          newHydrationStatus.set(taskId, "error");
          return { ...current, hydrationStatus: newHydrationStatus };
        });
      } finally {
        hydrationInFlight.current.delete(taskId);
      }
    },
    [state.hydrationStatus],
  );

  const getHydrationStatus = useCallback(
    (taskId: string): HydrationStatus =>
      state.hydrationStatus.get(taskId) ?? "idle",
    [state.hydrationStatus],
  );

  // Get stream config: dynamic config if available, otherwise fall back to registry
  const getStreamConfigForTask = useCallback(
    (
      taskId: string,
      streamId: string,
    ): EventStreamConfig | DynamicStreamConfig => {
      const taskConfigs = state.streamConfigsByTask.get(taskId);
      const dynamicConfig = taskConfigs?.get(streamId);
      if (dynamicConfig) {
        return dynamicConfig;
      }
      // Fall back to static registry
      return getStreamConfig(streamId);
    },
    [state.streamConfigsByTask],
  );

  // Get all stream configs for a task, merging dynamic with static fallbacks
  const getStreamConfigsForTask = useCallback(
    (taskId: string): Map<string, EventStreamConfig | DynamicStreamConfig> => {
      const events = state.eventsByTask.get(taskId) ?? [];
      const taskConfigs =
        state.streamConfigsByTask.get(taskId) ??
        new Map<string, DynamicStreamConfig>();

      // Get unique stream IDs from events
      const streamIds = new Set<string>();
      for (const event of events) {
        streamIds.add(event.stream_id);
      }

      // Build config map: dynamic config if available, otherwise static
      const result = new Map<string, EventStreamConfig | DynamicStreamConfig>();
      for (const streamId of streamIds) {
        const dynamicConfig = taskConfigs.get(streamId);
        result.set(streamId, dynamicConfig ?? getStreamConfig(streamId));
      }

      return result;
    },
    [state.eventsByTask, state.streamConfigsByTask],
  );

  return {
    clearAll,
    clearTask,
    getEventsForTask,
    getHydrationStatus,
    getLatestEvent,
    getStreamConfig: getStreamConfigForTask,
    getStreamConfigsForTask,
    hydrateTask,
  };
}
